import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Button from '../../components/base/Button';
import API_BASE_URL from '../../config/api';

type ReservedItem = {
  pdf_client_name: string;
  job_type: string;
};

export default function ReservedFormats() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReservedItem[]>([]);
  const [jobFilter, setJobFilter] = useState<'all' | 'exp' | 'imp'>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await fetch(`${API_BASE_URL}/reserved-pdf-clients`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        const data = await resp.json();
        if (!resp.ok || !data.success) {
          setError(data.error || 'Failed to load reserved formats');
          setItems([]);
          return;
        }
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load reserved formats');
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (jobFilter !== 'all' && item.job_type !== jobFilter) return false;
      if (!q) return true;
      return item.pdf_client_name.toLowerCase().includes(q);
    });
  }, [items, jobFilter, search]);

  const expCount = items.filter((i) => i.job_type === 'exp').length;
  const impCount = items.filter((i) => i.job_type === 'imp').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />

      <div className="px-4 py-6">
        <Button variant="outline" onClick={() => navigate('/parser-creation')} className="mb-6">
          Back
        </Button>
      </div>

      <div className="flex flex-col items-center px-4 pb-12">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Reserved Formats</h1>
            <p className="text-gray-600">
              These PDF client names are used by hardcoded EXP/IMP formats. Do not create a new parser with the same name.
            </p>
            <div className="w-full h-px bg-gray-300 mt-4"></div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
              <div className="flex gap-2">
                {([
                  ['all', `All (${items.length})`],
                  ['exp', `EXP (${expCount})`],
                  ['imp', `IMP (${impCount})`],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setJobFilter(key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer ${
                      jobFilter === key
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client name..."
                className="flex-1 px-3 py-2 border-2 border-cyan-400 rounded-lg focus:outline-none focus:border-indigo-600"
              />
            </div>

            {loading && <p className="text-gray-500 text-sm">Loading...</p>}
            {error && !loading && <p className="text-red-600 text-sm">{error}</p>}

            {!loading && !error && (
              <>
                <p className="text-sm text-gray-500 mb-3">
                  Showing {filtered.length} name{filtered.length === 1 ? '' : 's'}
                </p>
                <div className="max-h-[28rem] overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 font-semibold text-indigo-600">PDF Client Name</th>
                        <th className="px-4 py-2 font-semibold text-indigo-600 w-24">Job Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((item) => (
                        <tr
                          key={`${item.job_type}-${item.pdf_client_name}`}
                          className="border-t border-gray-100 hover:bg-gray-50"
                        >
                          <td className="px-4 py-2 text-gray-800">{item.pdf_client_name}</td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-medium uppercase ${
                                item.job_type === 'exp'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {item.job_type}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-4 py-6 text-center text-gray-500">
                            No matching names
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
